import type { Review } from 'src/components/restructure/product/types/review'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const hasReviewFields = (value: Record<string, unknown>) =>
  'totalReviews' in value ||
  'total_reviews' in value ||
  'reviewsCount' in value ||
  'totalRatingsAverageNote' in value ||
  'total_ratings_average_note' in value

const extractReviewPayload = (
  response: Record<string, unknown>
): Record<string, unknown> | null => {
  if (hasReviewFields(response)) {
    return response
  }

  const { data } = response

  if (!isRecord(data)) {
    return null
  }

  const graphqlReview = data.DktPdp_GetProductReview

  if (isRecord(graphqlReview)) {
    return graphqlReview
  }

  if (hasReviewFields(data)) {
    return data
  }

  return null
}

const normalizeNote = (
  note?: {
    count?: unknown
    countRecommended?: unknown
    count_recommended?: unknown
  } | null
) => ({
  count: Number(note?.count ?? 0),
  countRecommended: Number(
    note?.countRecommended ?? note?.count_recommended ?? 0
  ),
})

export const normalizeReviewResponse = (response: unknown): Review | null => {
  if (!isRecord(response)) {
    return null
  }

  const reviewPayload = extractReviewPayload(response)

  if (!reviewPayload) {
    return null
  }

  const totalRatingsAverageNote = Number(
    reviewPayload.totalRatingsAverageNote ??
      reviewPayload.total_ratings_average_note ??
      0
  )

  const totalReviews = Number(
    reviewPayload.totalReviews ??
      reviewPayload.total_reviews ??
      reviewPayload.reviewsCount ??
      0
  )

  const reviews = Array.isArray(reviewPayload.reviews)
    ? reviewPayload.reviews
    : []

  const notesPayload = isRecord(reviewPayload.notes) ? reviewPayload.notes : {}

  return {
    totalRatingsAverageNote,
    totalReviews,
    reviews,
    notes: {
      one: normalizeNote(notesPayload.one as Review['notes']['one']),
      two: normalizeNote(notesPayload.two as Review['notes']['two']),
      three: normalizeNote(notesPayload.three as Review['notes']['three']),
      four: normalizeNote(notesPayload.four as Review['notes']['four']),
      five: normalizeNote(notesPayload.five as Review['notes']['five']),
    },
  }
}
